import CheckOutline from 'assets/icons/CheckOutline';
import Button from 'common/components/Button';
import InlineLoader from 'common/components/InlineLoader';
import LabelledInput from 'common/components/LabelledInput';
import { Form, Formik } from 'formik';
import SimpleSwitch from 'offers/components/SimpleSwitch';
import React from 'react';
import { useIntegrationSettings } from 'settings/contexts/IntegrationSettingsContext';
import * as Yup from 'yup';

const DefaultIntegrationForm = () => {
  const {
    t,
    selectedIntegration,
    companySettings,
    handleSave,
    isUpdatingCompanySettings,
  } = useIntegrationSettings();

  const workableSetting = companySettings?.find(
    (setting) => setting.name === 'workable_api_key',
  );
  const subdomainSetting = companySettings?.find(
    (setting) => setting.name === 'workable_api_subdomain',
  );

  const isSapsfIntegrationSuiteEnabled =
    companySettings?.find(
      (setting) => setting.name === 'is_integration_suite_enabled',
    )?.value === 'true';
  const sapsfApiKeySetting = companySettings?.find(
    (setting) => setting.name === 'sapsf_api_key',
  );
  const sapsfApiSubdomainSetting = companySettings?.find(
    (setting) => setting.name === 'sapsf_api_subdomain',
  );
  const sapsfIntegrationSuiteApiKeySetting = companySettings?.find(
    (setting) => setting.name === 'sapsf_integration_suite_api_key',
  );
  const sapsfIntegrationSuiteApiSubdomainSetting = companySettings?.find(
    (setting) => setting.name === 'sapsf_integration_suite_api_subdomain',
  );
  const initialValues = {
    workable_api_key: workableSetting?.value || '',
    workable_api_subdomain: subdomainSetting?.value || '',
    sapsf_api_key: sapsfApiKeySetting?.value || '',
    sapsf_api_subdomain: sapsfApiSubdomainSetting?.value || '',
    sapsf_integration_suite_api_key:
      sapsfIntegrationSuiteApiKeySetting?.value || '',
    sapsf_integration_suite_api_subdomain:
      sapsfIntegrationSuiteApiSubdomainSetting?.value || '',
    is_integration_suite_enabled: isSapsfIntegrationSuiteEnabled ?? false,
  };

  const getValidationSchema = (integrationSlug, t) => {
    const prefix = integrationSlug.toLowerCase();
    const isSapsf = integrationSlug === 'SAPSF';
    return Yup.object({
      [`${prefix}_api_key`]: Yup.string(),
      [`${prefix}_api_subdomain`]: Yup.string(),
      ...(isSapsf
        ? { [`${prefix}_integration_suite_api_key`]: Yup.string() }
        : {}),
      ...(isSapsf
        ? { [`${prefix}_integration_suite_api_subdomain`]: Yup.string() }
        : {}),
      ...(isSapsf ? { [`is_integration_suite_enabled`]: Yup.boolean() } : {}),
    });
  };

  const validationSchema = getValidationSchema(selectedIntegration.slug, t);

  return (
    <div className='flex flex-col'>
      <h3 className='text-lg font-semibold text-gray-800 mb-4'>
        {t('connect-to')} {selectedIntegration.name}
      </h3>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnMount
        onSubmit={handleSave}
      >
        {({ getFieldProps, setFieldValue, isValid, dirty, values }) => {
          const integrationPrefix = selectedIntegration.slug.toLowerCase();
          const isSapsf = selectedIntegration?.slug === 'SAPSF';
          const integrationSuiteEnabled =
            isSapsf && values?.is_integration_suite_enabled;

          const apiKeyFieldName = integrationSuiteEnabled
            ? `${integrationPrefix}_integration_suite_api_key`
            : `${integrationPrefix}_api_key`;
          const apiSubdomainFieldName = integrationSuiteEnabled
            ? `${integrationPrefix}_integration_suite_api_subdomain`
            : `${integrationPrefix}_api_subdomain`;

          return (
            <Form>
              <div className='mb-4'>
                <LabelledInput
                  name={apiKeyFieldName}
                  label={t(
                    integrationSuiteEnabled
                      ? 'fields.integration-suite-api-key'
                      : 'fields.api-key',
                    {
                      name: selectedIntegration.name,
                    },
                  )}
                  type='text'
                  placeholder={t('fields.placeholder.api-key')}
                  {...getFieldProps(apiKeyFieldName)}
                />
              </div>

              <div className='mb-6'>
                <LabelledInput
                  name={apiSubdomainFieldName}
                  label={t(
                    integrationSuiteEnabled
                      ? 'fields.integration-suite-subdomain'
                      : 'fields.subdomain',
                    {
                      name: selectedIntegration.name,
                    },
                  )}
                  type='text'
                  placeholder={t('fields.placeholder.subdomain')}
                  {...getFieldProps(apiSubdomainFieldName)}
                />
              </div>

              {isSapsf && (
                <div className='mb-6'>
                  <div className='flex items-center gap-3'>
                    <SimpleSwitch
                      name='is_integration_suite_enabled'
                      isActive={integrationSuiteEnabled}
                      onToggle={() =>
                        setFieldValue(
                          'is_integration_suite_enabled',
                          !integrationSuiteEnabled,
                        )
                      }
                    />
                    <span className='text-sm font-medium text-gray-800'>
                      {t('fields.integration-suite-toggle-label')}
                    </span>
                  </div>
                </div>
              )}

              <Button
                type='submit'
                variant='primary'
                disabled={isUpdatingCompanySettings || !dirty || !isValid}
              >
                <div className='flex items-center gap-2'>
                  {t('save')}
                  {isUpdatingCompanySettings ? (
                    <InlineLoader size={14} />
                  ) : (
                    <CheckOutline size={14} strokeColor='white' />
                  )}
                </div>
              </Button>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default DefaultIntegrationForm;
